import { apiService } from './api.service';
import { ReportDetails, SalesContact } from '../types';

class ReportService {
  private static instance: ReportService;

  private constructor() {}

  static getInstance(): ReportService {
    if (!ReportService.instance) {
      ReportService.instance = new ReportService();
    }
    return ReportService.instance;
  }

  /**
   * Get report details for a project
   * @param projectId The project ID
   * @returns The report details
   */
  async getProjectReportDetails(projectId: string): Promise<ReportDetails> {
    try {
      return await apiService.getProjectReportDetails(projectId);
    } catch (error) {
      console.error('Error getting project report details:', error);
      throw error;
    }
  }

  /**
   * Update report details for a project
   * @param projectId The project ID
   * @param reportData The report data to update
   * @returns The updated report details
   */
  async updateProjectReportDetails(
    projectId: string,
    reportData: Partial<ReportDetails>
  ): Promise<ReportDetails> {
    try {
      return await apiService.updateProjectReportDetails(projectId, reportData);
    } catch (error) {
      console.error('Error updating project report details:', error);
      throw error;
    }
  }

  /**
   * Get sales contacts (users with @metarouter.io email)
   * @returns Array of sales contacts
   */
  async getSalesContacts(): Promise<SalesContact[]> {
    try {
      const contacts = await apiService.getSalesContacts();
      return contacts.map(contact => ({
        id: contact.id,
        name: contact.name,
        email: contact.email
      }));
    } catch (error) {
      console.error('Error getting sales contacts:', error);
      throw error;
    }
  }

  /**
   * Save report settings to localStorage for anonymous users
   * @param reportData The report data to save
   */
  saveReportToLocalStorage(reportData: ReportDetails): void {
    try {
      localStorage.setItem('reportSettings', JSON.stringify(reportData));
    } catch (error) {
      console.error('Error saving report to localStorage:', error);
    }
  }

  /**
   * Get report settings from localStorage for anonymous users
   * @returns The report settings or null if not found
   */
  getReportFromLocalStorage(): ReportDetails | null {
    try {
      const reportSettings = localStorage.getItem('reportSettings');
      return reportSettings ? JSON.parse(reportSettings) : null;
    } catch (error) {
      console.error('Error getting report from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear report settings from localStorage
   */
  clearReportFromLocalStorage(): void {
    try {
      localStorage.removeItem('reportSettings');
      localStorage.removeItem('diagram_image');
    } catch (error) {
      console.error('Error clearing report from localStorage:', error);
    }
  }

  /**
   * Transfer report settings from localStorage to database
   * @param projectId The project ID
   */
  async transferReportFromLocalStorageToDatabase(projectId: string): Promise<void> {
    try {
      const reportSettings = this.getReportFromLocalStorage();
      if (reportSettings) {
        await this.updateProjectReportDetails(projectId, reportSettings);
        this.clearReportFromLocalStorage();
      }
    } catch (error) {
      console.error('Error transferring report from localStorage to database:', error);
    }
  }
}

export const reportService = ReportService.getInstance();
